import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, RefreshCw, Send, TerminalSquare } from 'lucide-react';
import { api, ApiClientError, jsonBody } from './api';
import { Select } from './Select';
import { Modal } from './Modal';
import type { IdentifyCommand, IdentifyOption, Notify } from './types';

const STATUS_LABEL: Record<IdentifyCommand['status'], string> = {
  pending: '待拉取',
  delivered: '已下发',
  done: '已回传',
};

const STATUS_TONE: Record<IdentifyCommand['status'], string> = {
  pending: 'warn',
  delivered: 'neutral',
  done: 'ok',
};

// 固定周期的常用值；仍然允许直接输入任意秒数。
const INTERVAL_PRESETS = [
  { label: '1h', seconds: 3600 },
  { label: '3h', seconds: 10800 },
  { label: '6h', seconds: 21600 },
  { label: '12h', seconds: 43200 },
  { label: '24h', seconds: 86400 },
];

const errorText = (cause: unknown): string => (
  cause instanceof ApiClientError ? cause.message : cause instanceof Error ? cause.message : '操作失败'
);

function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('zh-CN', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

export function IdentifyConsole({ notify }: { notify: Notify }) {
  const [identifies, setIdentifies] = useState<IdentifyOption[]>([]);
  const [identify, setIdentify] = useState('');
  const [online, setOnline] = useState<boolean | null>(null);
  const [lastPullAt, setLastPullAt] = useState<string | null>(null);
  const [identifyOpen, setIdentifyOpen] = useState(false);
  const [identifyActive, setIdentifyActive] = useState(0);
  const identifyBoxRef = useRef<HTMLDivElement>(null);
  const [controlMode, setControlMode] = useState<'default' | 'none' | 'active' | 'fixed'>('none');
  const [activeInput, setActiveInput] = useState('10');
  const [fixedInput, setFixedInput] = useState('10800');
  const [commands, setCommands] = useState<IdentifyCommand[]>([]);
  const [commandInput, setCommandInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [resultFor, setResultFor] = useState<IdentifyCommand | null>(null);
  const generationRef = useRef(0);

  const identifyQuery = identify.trim().toLocaleLowerCase();
  const identifySuggestions = (
    identifyQuery
      ? identifies.filter((item) => item.identify.toLocaleLowerCase().includes(identifyQuery))
      : identifies
  ).slice(0, 20);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!identifyBoxRef.current?.contains(event.target as Node)) setIdentifyOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  function chooseIdentify(value: string) {
    setIdentify(value);
    setIdentifyOpen(false);
    setIdentifyActive(0);
  }

  const loadIdentifies = useCallback(async () => {
    try {
      const data = await api<{ identifies: IdentifyOption[] }>('/api/identify-cmds/identifies');
      setIdentifies(data.identifies ?? []);
    } catch (cause) {
      notify('error', errorText(cause));
    }
  }, [notify]);

  const loadCommands = useCallback(async (silent = false) => {
    const target = identify.trim();
    if (!target) {
      setCommands([]);
      setLoading(false);
      return;
    }
    const generation = ++generationRef.current;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({ identify: target, limit: '100' });
      const data = await api<{
        items: IdentifyCommand[];
        online: boolean;
        last_pull_at: string | null;
      }>(`/api/identify-cmds?${params.toString()}`);
      if (generation !== generationRef.current) return;
      setCommands(data.items ?? []);
      setOnline(Boolean(data.online));
      setLastPullAt(data.last_pull_at ?? null);
      setError('');
    } catch (cause) {
      if (generation !== generationRef.current) return;
      setError(errorText(cause));
      if (!silent) notify('error', errorText(cause));
    } finally {
      if (generation === generationRef.current) setLoading(false);
    }
  }, [identify, notify]);

  useEffect(() => { void loadIdentifies(); }, [loadIdentifies]);

  useEffect(() => {
    setOnline(null);
    setLastPullAt(null);
  }, [identify]);

  useEffect(() => {
    // Identify 输入做 300ms 防抖；自动刷新与输入变更共用同一个清理函数。
    const debounce = window.setTimeout(() => { void loadCommands(false); }, 300);
    const timer = window.setInterval(() => { void loadCommands(true); }, 5000);
    return () => {
      window.clearTimeout(debounce);
      window.clearInterval(timer);
    };
  }, [loadCommands]);

  async function sendCommand() {
    const target = identify.trim();
    const command = commandInput.trim();
    if (!target || !command || sending) return;
    const control: Record<string, number> = {};
    if (controlMode === 'default') {
      control.interval = 0;
    } else if (controlMode === 'active') {
      const active = Number(activeInput);
      if (!Number.isInteger(active) || active < 2 || active > 10800) {
        notify('error', 'active 必须在 2..10800 秒');
        return;
      }
      control.active = active;
    } else if (controlMode === 'fixed') {
      const interval = Number(fixedInput);
      if (!Number.isInteger(interval) || interval < 100) {
        notify('error', 'interval 固定周期必须大于等于 100 秒');
        return;
      }
      control.interval = interval;
    }
    setSending(true);
    try {
      const body: Record<string, unknown> = { identify: target, command, ...control };
      await api<{ command: IdentifyCommand }>('/api/identify-cmds', {
        method: 'POST',
        body: jsonBody(body),
      });
      setCommandInput('');
      setControlMode('none');
      setIdentifies((current) => (current.some((item) => item.identify === target)
        ? current
        : [...current, { identify: target, online: online ?? false, last_pull_at: lastPullAt }]
          .sort((left, right) => left.identify.localeCompare(right.identify))));
      await loadCommands(false);
      const controlText = controlMode === 'default'
        ? '默认 pull 周期（interval=0）'
        : controlMode === 'active'
          ? `激活 active=${control.active}`
          : controlMode === 'fixed'
            ? `固定 interval=${control.interval}`
            : '';
      notify('success', controlText ? `命令已入队，并下发${controlText}` : '命令已入队，等待执行端拉取');
    } catch (cause) {
      notify('error', errorText(cause));
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="identify-console">
      <div className="section-head">
        <div>
          <h3>Identify 控制台</h3>
          <p>独立于设备控制面与 allow_exec 的低权限命令通道；Identify 本身就是命令凭据，请使用随机值。</p>
        </div>
        <div className="section-actions">
          {identify.trim() && (
            <span className={`status-pill ${online === null ? 'neutral' : online ? 'ok' : 'neutral'}`} title={lastPullAt ? `上次拉取 ${formatTime(lastPullAt)}` : '尚未拉取'}>
              {online === null ? '状态未知' : online ? '在线' : '离线'}
            </span>
          )}
          {identify.trim() && <span className="count-chip">上次拉取 {lastPullAt ? formatTime(lastPullAt) : '从未'}</span>}
          <span className="count-chip">{commands.length} 条命令</span>
          <button type="button" className="secondary-button small" disabled={loading || !identify.trim()} onClick={() => void loadCommands(false)}>
            <RefreshCw className={loading ? 'spin' : ''} size={14} />刷新
          </button>
        </div>
      </div>

      <div className="panel identify-console-form">
        <div className="identify-console-field identify-console-identify">
          <span>Identify</span>
          <div className="identify-combobox" ref={identifyBoxRef}>
            <input
              id="identify-input"
              role="combobox"
              aria-label="Identify"
              aria-autocomplete="list"
              aria-expanded={identifyOpen}
              aria-controls="identify-suggestions"
              aria-activedescendant={identifyOpen && identifySuggestions[identifyActive] ? `identify-suggestion-${identifyActive}` : undefined}
              autoComplete="off"
              value={identify}
              onFocus={() => { setIdentifyOpen(true); setIdentifyActive(0); }}
              onChange={(event) => { setIdentify(event.target.value); setIdentifyOpen(true); setIdentifyActive(0); }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') { setIdentifyOpen(false); return; }
                if (event.key === 'Tab') { setIdentifyOpen(false); return; }
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault();
                  if (!identifyOpen) { setIdentifyOpen(true); return; }
                  if (!identifySuggestions.length) return;
                  const step = event.key === 'ArrowDown' ? 1 : -1;
                  setIdentifyActive((current) => (current + step + identifySuggestions.length) % identifySuggestions.length);
                  return;
                }
                if (event.key === 'Enter' && identifyOpen && identifySuggestions[identifyActive]) {
                  event.preventDefault();
                  chooseIdentify(identifySuggestions[identifyActive].identify);
                }
              }}
              placeholder="输入或选择 Identify"
            />
            <ChevronDown className="identify-combobox-icon" size={14} aria-hidden="true" />
            {identifyOpen && (
              <div className="identify-suggest-popup" id="identify-suggestions" role="listbox" aria-label="Identify 建议">
                {identifySuggestions.length > 0 ? identifySuggestions.map((item, index) => (
                  <button
                    key={item.identify}
                    id={`identify-suggestion-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === identifyActive}
                    className={`identify-suggest-option ${index === identifyActive ? 'active' : ''}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseMove={() => setIdentifyActive(index)}
                    onClick={() => chooseIdentify(item.identify)}
                  >
                    <span className="mono">{item.identify}</span>
                    <span className={`identify-suggest-state ${item.online ? 'online' : ''}`}>
                      {item.online ? '在线' : '离线'}
                    </span>
                  </button>
                )) : (
                  <div className="identify-suggest-empty">没有匹配项，直接输入即可</div>
                )}
              </div>
            )}
          </div>
          <small>任意字符串；只有持有同一 Identify 的执行端会拉到命令。在线判断：上次 pull 距现在 &lt; 5 小时。</small>
        </div>

        <label className="identify-console-field identify-console-command">
          <span>命令</span>
          <textarea
            rows={3}
            value={commandInput}
            onChange={(event) => setCommandInput(event.target.value)}
            placeholder="例如：uname -a && uptime"
            aria-label="命令内容"
          />
          <small>单条最多 8192 字符；不能包含单独一行 ====。</small>
        </label>

        <div className="identify-console-controls">
          <div className="identify-control-main">
            <span className="identify-control-label">控制</span>
            <Select
              className="identify-control-select"
              value={controlMode}
              onChange={(event) => setControlMode(event.target.value as 'default' | 'none' | 'active' | 'fixed')}
              aria-label="控制指令"
            >
              <option value="none">不附带</option>
              <option value="default">默认 interval</option>
              <option value="fixed">固定 interval</option>
              <option value="active">激活 active</option>
            </Select>
            {controlMode === 'active' && (
              <>
                <input
                  type="number"
                  className="identify-interval-input"
                  min={2}
                  max={10800}
                  step={1}
                  value={activeInput}
                  onChange={(event) => setActiveInput(event.target.value)}
                  aria-label="active 起始周期（秒）"
                />
                <span className="identify-interval-unit">秒</span>
              </>
            )}
            {controlMode === 'fixed' && (
              <>
                <input
                  type="number"
                  className="identify-interval-input"
                  min={100}
                  step={1}
                  value={fixedInput}
                  onChange={(event) => setFixedInput(event.target.value)}
                  aria-label="interval 固定周期（秒）"
                />
                <span className="identify-interval-unit">秒</span>
                <div className="identify-interval-presets" role="group" aria-label="固定周期常用值">
                  {INTERVAL_PRESETS.map((preset) => (
                    <button
                      key={preset.seconds}
                      type="button"
                      className={`identify-preset${Number(fixedInput) === preset.seconds ? ' active' : ''}`}
                      aria-pressed={Number(fixedInput) === preset.seconds}
                      onClick={() => setFixedInput(String(preset.seconds))}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <small className="identify-control-hint">{controlMode === 'default'
            ? 'interval=0：执行端收到后按自己的配置文件 / -J 参数默认周期拉取'
            : controlMode === 'active'
              ? `active=${activeInput || '?'}：有命令保持该值，空拉取 +10 秒至 3 小时`
              : controlMode === 'fixed'
                ? `interval=${fixedInput || '?'}：固定周期，最小 100 秒，无上限`
                : '不附带 CONTROL 控制块'}</small>
          <button
            type="button"
            className="primary-button"
            disabled={sending || !identify.trim() || !commandInput.trim()}
            onClick={() => void sendCommand()}
          >
            <Send size={15} />{sending ? '发送中…' : '发送命令'}
          </button>
        </div>
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}

      <div className="panel tenant-table-wrap" aria-busy={loading}>
        <div className="tenant-table-head identify-cmd-grid">
          <span>cmd_id</span>
          <span>状态</span>
          <span>命令</span>
          <span>结果</span>
          <span>时间</span>
          <span>操作</span>
        </div>

        {!identify.trim() && (
          <div className="empty-state">
            <span className="empty-icon"><TerminalSquare size={22} /></span>
            <strong>先输入或选择一个 Identify</strong>
            <p>命令与设备记录完全无关，只要执行端用同一 Identify 拉取即可。</p>
          </div>
        )}

        {identify.trim() && loading && commands.length === 0 && (
          <div className="panel-loader" role="status">正在加载命令…</div>
        )}

        {identify.trim() && !loading && commands.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon"><TerminalSquare size={22} /></span>
            <strong>还没有命令</strong>
            <p>{'发送后执行端下次 GET /api/id/<identify>/cmd 时拉取。'}</p>
          </div>
        )}

        {commands.map((item) => (
          <div className="tenant-table-row identify-cmd-grid" key={item.cmd_id}>
            <span className="mono">#{item.cmd_id}</span>
            <span className="identify-cmd-status">
              <span className={`status-pill ${STATUS_TONE[item.status]}`}>{STATUS_LABEL[item.status]}</span>
              {item.control_name === 'active' && (
                <span className="tag">激活 {item.control_value}s</span>
              )}
              {item.control_name === 'interval' && (
                <span className="tag">{item.control_value === 0 ? '默认周期' : `固定 ${item.control_value}s`}</span>
              )}
            </span>
            <pre className="identify-cmd-text">{item.command}</pre>
            <pre className="identify-cmd-text">{item.result ?? '—'}</pre>
            <span className="identify-cmd-times">
              <small>创建 {formatTime(item.created_at)}</small>
              <small>下发 {formatTime(item.delivered_at)}</small>
              <small>回传 {formatTime(item.result_received_at)}</small>
            </span>
            <span>
              <button
                type="button"
                className="text-button"
                disabled={!item.result}
                aria-label={`查看命令 ${item.cmd_id} 的结果`}
                onClick={() => setResultFor(item)}
              >查看</button>
            </span>
          </div>
        ))}
      </div>

      {resultFor && (
        <Modal
          title={`命令 #${resultFor.cmd_id} 结果`}
          subtitle={`${resultFor.identify} · ${STATUS_LABEL[resultFor.status]}`}
          size="wide-modal"
          onClose={() => setResultFor(null)}
        >
          <div className="identify-cmd-command">
            <span>命令</span>
            <pre>{resultFor.command}</pre>
          </div>
          <div className="identify-cmd-command">
            <span>结果</span>
            <pre>{resultFor.result || '(空结果)'}</pre>
          </div>
        </Modal>
      )}
    </section>
  );
}
